'use client';

import Modal from '@/components/ui/modal';
import { useLocale, useTranslations } from 'next-intl';
import { TechnicalInfoModalProps as Props } from '../interfaces';

export const TechnicalInfoModal: React.FC<Props> = ({
    onClose,
    isOpen,
    resolution,
    aspectRatio,
    videoBitrate,
    audioBitrate,
    audioSampleRate,
    audioMode,
}) => {
    const t = useTranslations('ScreeningRoom.modal.technicalInfo');
    const locale = useLocale();

    const formatBitrate = (value: number): string => {
        if (!Number.isFinite(value)) return '';

        return new Intl.NumberFormat(locale, {
            maximumFractionDigits: 0,
        })
            .format(value)
            .replace(/\u202F/g, ' ');
    };

    let audioModeLabel = '';
    if (audioMode === 'stereo') {
        audioModeLabel = t('stereo');
    } else if (audioMode === 'mono') {
        audioModeLabel = t('mono');
    } else if (audioMode) {
        const match = audioMode.match(/^(\d+)ch$/i);
        audioModeLabel = t('multiplesChannels', { channels: match ? match[1] : audioMode });
    }

    return (
        <Modal
            customClass="inset-x-0 top-0 bottom-0 z-50 transition-all duration-300 ease-in-outpy-3"
            isOpen={isOpen}
            onClose={onClose}
            ModalSize="max-w-xl"
        >
            <div className="color-white">
                <div className="text-center font-bold text-2xl mb-8">{t('title')}</div>

                <div className="max-w-md mx-auto text-left">
                    <div className="mb-6">
                        <div className="font-semibold text-lg mb-3">Video</div>
                        <ul className="list-disc list-inside leading-[1.6]">
                            {!!resolution && (
                                <li>
                                    {t.rich('resolution', {
                                        resolution,
                                    })}
                                </li>
                            )}
                            {!!aspectRatio && (
                                <li>
                                    {t.rich('aspectRatio', {
                                        aspectRatio,
                                    })}
                                </li>
                            )}
                            {!!videoBitrate && (
                                <li>
                                    {t.rich('videoBitrate', {
                                        videoBitrate: formatBitrate(videoBitrate),
                                    })}
                                </li>
                            )}
                        </ul>
                    </div>

                    <div>
                        <div className="font-semibold text-lg mb-3">Audio</div>
                        <ul className="list-disc list-inside leading-[1.6]">
                            {!!audioMode && (
                                <li>
                                    {t.rich('audioMode', {
                                        audioMode: audioModeLabel,
                                    })}
                                </li>
                            )}
                            {!!audioSampleRate && (
                                <li>
                                    {t.rich('audioSampleRate', {
                                        audioSampleRate: formatBitrate(audioSampleRate),
                                    })}
                                </li>
                            )}
                            {!!audioBitrate && (
                                <li>
                                    {t.rich('audioBitrate', {
                                        audioBitrate: audioBitrate,
                                    })}
                                </li>
                            )}
                        </ul>
                    </div>
                </div>
            </div>
        </Modal>
    );
};
